const MOMENTO_HERO_IMAGE_DWELL_MS = 10000;
const MOMENTO_HERO_FINAL_VIDEO_END_SECONDS = 25;

if (!customElements.get('momento-hero')) {
  class MomentoHero extends HTMLElement {
    constructor() {
      super();

      this.activeSlide = 0;
      this.settledSlide = 0;
      this.stillTimer = null;
      this.settleTimer = null;
      this.videoTimer = null;
      this.finalEndpointTimer = null;
      this.videoObjectUrl = null;
      this.campaignReady = false;
      this.drag = null;
      this.wheelLocked = false;
      this.wheelTimer = null;
      this.abortController = null;
      this.desktopSequence = window.matchMedia('(min-width: 1024px)');
    }

    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.carousel = this.querySelector('[data-hero-carousel]');
      this.track = this.querySelector('[data-hero-track]');
      this.allSlides = [...this.querySelectorAll('[data-hero-slide]')];
      this.allSelectors = [...this.querySelectorAll('[data-hero-select]')];
      this.liveRegion = this.querySelector('[data-hero-live]');
      this.initialImage = this.querySelector('[data-hero-initial-image]');
      this.portraitImage = this.querySelector('[data-hero-portrait-image]');
      this.introVideo = this.querySelector('[data-hero-intro-video]');
      this.finalVideo = this.querySelector('[data-hero-final-video]');
      this.finalOverlay = this.querySelector('[data-hero-final-overlay]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.carousel || !this.track || this.allSlides.length !== 5) return;

      this.configureSequence();
      this.bindEvents();
      this.updateSlideState();
      this.prepareInitialSlide();
      this.prepareFinalVideo();
    }

    disconnectedCallback() {
      this.clearTimers();
      this.abortController?.abort();
      this.abortController = null;
      this.introVideo?.pause();
      this.finalVideo?.pause();

      if (this.videoObjectUrl) URL.revokeObjectURL(this.videoObjectUrl);
      this.videoObjectUrl = null;
      window.clearTimeout(this.wheelTimer);
      this.wheelTimer = null;
    }

    configureSequence() {
      const mediaOrder = this.desktopSequence.matches
        ? ['red', 'blue', 'campaign', 'roller-rink']
        : ['red', 'blue', 'campaign', 'roller-rink'];
      const slideByMedia = new Map(this.allSlides.map((slide) => [slide.dataset.heroMedia, slide]));
      const selectorByMedia = new Map(this.allSelectors.map((selector) => [selector.dataset.heroMedia, selector]));

      this.slides = mediaOrder.map((media) => slideByMedia.get(media)).filter(Boolean);
      this.selectors = mediaOrder.map((media) => selectorByMedia.get(media)).filter(Boolean);

      this.slides.forEach((slide, index) => {
        const label = slide.dataset.heroLabel || 'MOMENTO campaign';
        slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}: ${label}`);
      });
      this.selectors.forEach((selector, index) => {
        const slide = this.slides[index];
        selector.dataset.heroSelect = String(index);
        selector.setAttribute('aria-label', `Go to slide ${index + 1}: ${slide?.dataset.heroLabel || 'MOMENTO campaign'}`);
      });
    }

    activeMedia() {
      return this.slides[this.activeSlide]?.dataset.heroMedia;
    }

    settledMedia() {
      return this.slides[this.settledSlide]?.dataset.heroMedia;
    }

    mediaIndex(media) {
      return this.slides.findIndex((slide) => slide.dataset.heroMedia === media);
    }

    handleSequenceChange() {
      this.clearTimers();
      this.pauseVideos();
      this.configureSequence();
      this.activeSlide = 0;
      this.settledSlide = 0;
      this.track.style.setProperty('--hero-active-slide', '0');
      this.updateSlideState();
      this.startActiveMedia();
    }

    bindEvents() {
      const listenerOptions = { signal: this.signal };

      this.allSelectors.forEach((selector) => {
        selector.addEventListener('click', () => {
          this.selectSlide(this.mediaIndex(selector.dataset.heroMedia));
        }, listenerOptions);
        selector.addEventListener('pointerdown', (event) => event.stopPropagation(), listenerOptions);
      });

      this.querySelectorAll('[data-hero-interactive]').forEach((element) => {
        element.addEventListener('pointerdown', (event) => event.stopPropagation(), listenerOptions);
      });

      this.carousel.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.selectSlide(Math.max(0, this.activeSlide - 1));
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.selectSlide(Math.min(this.slides.length - 1, this.activeSlide + 1));
        }
      }, listenerOptions);

      this.carousel.addEventListener('wheel', (event) => this.handleWheel(event), {
        passive: false,
        signal: this.signal,
      });
      this.carousel.addEventListener('pointerdown', (event) => this.handlePointerDown(event), listenerOptions);
      this.carousel.addEventListener('pointermove', (event) => this.handlePointerMove(event), listenerOptions);
      this.carousel.addEventListener('pointerup', (event) => this.handlePointerEnd(event), listenerOptions);
      this.carousel.addEventListener('pointercancel', (event) => this.handlePointerCancel(event), listenerOptions);
      this.track.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'transform') this.settleSlide();
      }, listenerOptions);

      this.introVideo?.addEventListener('ended', () => this.advanceAfterVideo(this.mediaIndex('red')), listenerOptions);
      this.introVideo?.addEventListener('error', () => this.scheduleVideoAdvance(3000, this.mediaIndex('red')), listenerOptions);
      this.introVideo?.addEventListener('canplay', () => {
        if (this.activeMedia() === 'floating-video' && this.introVideo.paused) this.playIntroVideo(false);
      }, listenerOptions);
      this.finalVideo?.addEventListener('ended', () => this.advanceAfterVideo(0), listenerOptions);
      this.finalVideo?.addEventListener('timeupdate', () => this.handleFinalVideoTimeUpdate(), listenerOptions);
      this.finalVideo?.addEventListener('error', () => this.scheduleVideoAdvance(2400, 0), listenerOptions);
      this.finalVideo?.addEventListener('canplay', () => {
        if (this.activeMedia() === 'roller-rink' && this.finalVideo.paused) this.playFinalVideo(false);
      }, listenerOptions);
      document.addEventListener('visibilitychange', () => this.handleVisibility(), listenerOptions);
      this.reducedMotion.addEventListener('change', () => this.handleMotionPreference(), listenerOptions);
      this.desktopSequence.addEventListener('change', () => this.handleSequenceChange(), listenerOptions);
    }

    prepareInitialSlide() {
      const markReady = () => {
        if (this.campaignReady) return;
        this.campaignReady = true;
        this.scheduleStillAdvance();
      };

      if (this.initialImage?.complete) {
        this.initialImage.decode?.().catch(() => {}).finally(markReady);
      } else {
        this.initialImage?.addEventListener('load', markReady, { once: true, signal: this.signal });
        this.initialImage?.addEventListener('error', markReady, { once: true, signal: this.signal });
      }
    }

    async prepareFinalVideo() {
      const sourceData = this.querySelector('[data-hero-video-parts]');
      if (!sourceData || !this.finalVideo) return;

      try {
        const sources = JSON.parse(sourceData.textContent);
        const mode = window.matchMedia('(max-width: 680px)').matches ? 'mobile' : 'desktop';
        const urls = sources[mode];
        this.dataset.heroVideoMode = mode;

        const segments = await Promise.all(urls.map(async (url) => {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Hero video segment failed: ${response.status}`);
          return response.arrayBuffer();
        }));

        if (!this.isConnected) return;
        this.videoObjectUrl = URL.createObjectURL(new Blob(segments, { type: 'video/mp4' }));
        this.finalVideo.src = this.videoObjectUrl;
        this.finalVideo.load();

        if (this.activeMedia() === 'roller-rink' && !document.hidden) this.playFinalVideo(true);
      } catch (_error) {
        this.dataset.heroVideoMode = 'poster';
        if (this.activeMedia() === 'roller-rink') this.scheduleVideoAdvance(2400, 0);
      }
    }

    selectSlide(nextSlide) {
      if (!Number.isInteger(nextSlide) || nextSlide < 0 || nextSlide >= this.slides.length) return;

      this.clearTimers();
      const currentMedia = this.activeMedia();
      const nextMedia = this.slides[nextSlide]?.dataset.heroMedia;
      if (currentMedia === 'floating-video' && nextMedia !== 'floating-video') this.introVideo?.pause();
      if (currentMedia === 'roller-rink' && nextMedia !== 'roller-rink') {
        this.finalVideo?.pause();
        if (this.finalVideo) this.finalVideo.currentTime = 0;
      }

      if (nextSlide === this.activeSlide) {
        this.settleSlide();
        if (nextMedia === 'floating-video') window.requestAnimationFrame(() => this.playIntroVideo(true));
        else if (nextMedia === 'roller-rink') window.requestAnimationFrame(() => this.playFinalVideo(true));
        return;
      }

      this.activeSlide = nextSlide;
      this.track.style.setProperty('--hero-active-slide', String(nextSlide));
      this.updateSlideState();

      if (this.reducedMotion.matches) {
        this.settleSlide();
      } else {
        this.settleTimer = window.setTimeout(() => this.settleSlide(), 820);
      }

      if (nextMedia === 'floating-video') window.requestAnimationFrame(() => this.playIntroVideo(true));
      if (nextMedia === 'roller-rink') window.requestAnimationFrame(() => this.playFinalVideo(true));
    }

    settleSlide() {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
      this.settledSlide = this.activeSlide;
      this.updateSlideState();
      this.scheduleStillAdvance();
    }

    updateSlideState() {
      this.allSlides.forEach((slide) => {
        const index = this.slides.indexOf(slide);
        const active = index === this.activeSlide;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.toggleAttribute('inert', !active);
      });

      this.allSelectors.forEach((selector) => {
        const index = this.selectors.indexOf(selector);
        selector.setAttribute('aria-current', index === this.settledSlide ? 'true' : 'false');
        selector.setAttribute('aria-hidden', index === -1 ? 'true' : 'false');
        selector.tabIndex = index === -1 ? -1 : 0;
      });

      this.initialImage?.classList.toggle('hero-campaign-image--active', this.settledMedia() === 'campaign');
      this.portraitImage?.classList.toggle('hero-campaign-image--active', this.activeMedia() === 'blue');
      this.finalOverlay?.classList.toggle('hero-slide-overlay--active', this.activeMedia() === 'roller-rink');

      this.querySelectorAll('[data-hero-interactive]').forEach((element) => {
        const parentSlide = element.closest('[data-hero-slide]');
        element.tabIndex = this.slides.indexOf(parentSlide) === this.activeSlide ? 0 : -1;
      });

      if (this.liveRegion) this.liveRegion.textContent = `Hero slide ${this.settledSlide + 1} of ${this.slides.length}`;
    }

    startActiveMedia() {
      if (document.hidden) return;

      if (this.activeMedia() === 'floating-video') {
        this.playIntroVideo(false);
      } else if (this.activeMedia() === 'roller-rink') {
        this.playFinalVideo(false);
      } else {
        this.scheduleStillAdvance();
      }
    }

    playIntroVideo(restart) {
      if (!this.introVideo || this.activeMedia() !== 'floating-video' || document.hidden) return;
      if (restart) this.introVideo.currentTime = 0;
      this.scheduleVideoAdvance(3000, this.mediaIndex('red'));
      this.introVideo.play().catch(() => {});
    }

    playFinalVideo(restart) {
      if (!this.finalVideo?.src || this.activeMedia() !== 'roller-rink' || document.hidden) return;

      if (restart) this.finalVideo.currentTime = 0;
      this.scheduleVideoAdvance(5000, 0, true);
      this.finalVideo.play().then(() => {
        this.scheduleFinalVideoEndpoint();
      }).catch(() => {
        if (this.activeMedia() !== 'roller-rink') return;
        window.clearTimeout(this.finalEndpointTimer);
        this.finalEndpointTimer = null;
        this.scheduleVideoAdvance(2400, 0);
      });
    }

    handleFinalVideoTimeUpdate() {
      if (
        this.activeMedia() !== 'roller-rink'
        || !this.finalVideo
        || this.finalVideo.currentTime < MOMENTO_HERO_FINAL_VIDEO_END_SECONDS
      ) return;

      window.clearTimeout(this.finalEndpointTimer);
      this.finalEndpointTimer = null;
      this.finalVideo.pause();
      this.finalVideo.currentTime = MOMENTO_HERO_FINAL_VIDEO_END_SECONDS;
      this.selectSlide(0);
    }

    scheduleFinalVideoEndpoint() {
      window.clearTimeout(this.finalEndpointTimer);
      this.finalEndpointTimer = null;

      if (!this.finalVideo || this.activeMedia() !== 'roller-rink' || document.hidden) return;
      const remainingSeconds = Math.max(0, MOMENTO_HERO_FINAL_VIDEO_END_SECONDS - this.finalVideo.currentTime);
      this.finalEndpointTimer = window.setTimeout(() => {
        if (!this.finalVideo || this.activeMedia() !== 'roller-rink') return;
        this.finalVideo.pause();
        this.finalVideo.currentTime = MOMENTO_HERO_FINAL_VIDEO_END_SECONDS;
        this.selectSlide(0);
      }, remainingSeconds * 1000);
    }

    scheduleStillAdvance() {
      window.clearTimeout(this.stillTimer);
      this.stillTimer = null;

      if (document.hidden || this.reducedMotion.matches) return;
      const media = this.activeMedia();
      if (media === 'campaign' && !this.campaignReady) return;
      if (this.activeSlide !== this.settledSlide) return;
      if (!['campaign', 'red', 'blue'].includes(media)) return;

      this.stillTimer = window.setTimeout(() => {
        this.selectSlide(Math.min(this.slides.length - 1, this.activeSlide + 1));
      }, MOMENTO_HERO_IMAGE_DWELL_MS);
    }

    desktopDwellOffset() {
      return window.matchMedia('(min-width: 1024px)').matches ? 2000 : 0;
    }

    mobileDwellOffset() {
      return window.matchMedia('(min-width: 1024px)').matches ? 0 : 3000;
    }

    advanceAfterVideo(destination) {
      if (this.desktopDwellOffset() === 0 && this.mobileDwellOffset() === 0) {
        this.selectSlide(destination);
        return;
      }

      this.scheduleVideoAdvance(0, destination);
    }

    scheduleVideoAdvance(delay, destination, readinessFallback = false) {
      window.clearTimeout(this.videoTimer);
      this.videoTimer = window.setTimeout(() => {
        if (readinessFallback && !this.finalVideo?.error && this.finalVideo?.readyState !== 0) return;
        this.selectSlide(destination);
      }, delay + this.desktopDwellOffset() + this.mobileDwellOffset());
    }

    pauseVideos() {
      this.introVideo?.pause();
      this.finalVideo?.pause();
      window.clearTimeout(this.finalEndpointTimer);
      this.finalEndpointTimer = null;
    }

    handleVisibility() {
      window.clearTimeout(this.stillTimer);
      this.stillTimer = null;

      if (document.hidden) {
        this.pauseVideos();
        return;
      }

      this.startActiveMedia();
    }

    handleMotionPreference() {
      this.clearTimers();
      this.settleSlide();
      this.startActiveMedia();
    }

    handleWheel(event) {
      const horizontalDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.shiftKey ? event.deltaY : 0;
      if (Math.abs(horizontalDelta) < 16) return;
      if (this.wheelLocked) return;

      const destination = horizontalDelta > 0
        ? Math.min(this.slides.length - 1, this.activeSlide + 1)
        : Math.max(0, this.activeSlide - 1);
      if (destination === this.activeSlide) return;

      this.selectSlide(destination);
      this.wheelLocked = true;
      this.wheelTimer = window.setTimeout(() => {
        this.wheelLocked = false;
        this.wheelTimer = null;
      }, 720);
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      window.clearTimeout(this.stillTimer);
      this.stillTimer = null;
      window.clearTimeout(this.finalEndpointTimer);
      this.finalEndpointTimer = null;
      if (this.activeMedia() === 'floating-video') this.introVideo?.pause();
      if (this.activeMedia() === 'roller-rink') this.finalVideo?.pause();
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: performance.now(),
        direction: 'pending',
      };
      this.carousel.setPointerCapture?.(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const deltaX = event.clientX - this.drag.startX;
      const deltaY = event.clientY - this.drag.startY;

      if (this.drag.direction === 'pending' && Math.max(Math.abs(deltaX), Math.abs(deltaY)) > 8) {
        this.drag.direction = Math.abs(deltaX) > Math.abs(deltaY) * 1.12 ? 'horizontal' : 'vertical';
      }
      if (this.drag.direction !== 'horizontal') return;

      event.preventDefault();
      const atFirstEdge = this.activeSlide === 0 && deltaX > 0;
      const atLastEdge = this.activeSlide === this.slides.length - 1 && deltaX < 0;
      const resistedOffset = (atFirstEdge || atLastEdge) ? deltaX * 0.16 : deltaX;
      const dragOffset = Math.max(-this.carousel.clientWidth, Math.min(this.carousel.clientWidth, resistedOffset));
      this.track.classList.add('hero-visual-track--dragging');
      this.track.style.setProperty('--hero-drag-offset', `${dragOffset}px`);
      this.drag.lastX = event.clientX;
      this.drag.lastTime = performance.now();
    }

    handlePointerEnd(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const deltaX = event.clientX - this.drag.startX;
      const elapsed = Math.max(1, performance.now() - this.drag.lastTime);
      const velocity = (event.clientX - this.drag.lastX) / elapsed;
      const threshold = Math.max(52, this.carousel.clientWidth * 0.17);
      const shouldMove = this.drag.direction === 'horizontal' && (Math.abs(deltaX) > threshold || Math.abs(velocity) > 0.42);
      const destination = shouldMove
        ? this.activeSlide + (deltaX < 0 ? 1 : -1)
        : this.activeSlide;
      const committed = shouldMove && destination >= 0 && destination < this.slides.length;

      this.drag = null;
      this.carousel.releasePointerCapture?.(event.pointerId);
      this.track.classList.remove('hero-visual-track--dragging');
      this.track.style.removeProperty('--hero-drag-offset');

      if (committed) this.selectSlide(destination);
      else this.resumeActiveMedia();
    }

    handlePointerCancel(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      this.drag = null;
      this.track.classList.remove('hero-visual-track--dragging');
      this.track.style.removeProperty('--hero-drag-offset');
      this.resumeActiveMedia();
    }

    resumeActiveMedia() {
      if (this.activeMedia() === 'floating-video') this.playIntroVideo(false);
      else if (this.activeMedia() === 'roller-rink') this.playFinalVideo(false);
      else this.scheduleStillAdvance();
    }

    clearTimers() {
      window.clearTimeout(this.stillTimer);
      window.clearTimeout(this.settleTimer);
      window.clearTimeout(this.videoTimer);
      window.clearTimeout(this.finalEndpointTimer);
      this.stillTimer = null;
      this.settleTimer = null;
      this.videoTimer = null;
      this.finalEndpointTimer = null;
    }
  }

  customElements.define('momento-hero', MomentoHero);
}
